package dev.lingvo.site.components.release

import androidx.compose.runtime.Composable
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.attr
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.flexWrap
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import dev.lingvo.shared.ReleaseCheckFindingDto
import dev.lingvo.shared.ReleaseCheckReportDto
import dev.lingvo.site.ds.LBadge
import dev.lingvo.site.ds.LBadgeVariant
import dev.lingvo.site.ds.LButton
import dev.lingvo.site.ds.LButtonSize
import dev.lingvo.site.ds.LButtonVariant
import dev.lingvo.site.ds.LMuted
import dev.lingvo.site.ds.LSectionTitle
import dev.lingvo.site.ds.Space
import dev.lingvo.site.ds.formatCount
import dev.lingvo.site.ds.formatUtc
import dev.lingvo.site.i18n.t
import dev.lingvo.site.i18n.tf
import dev.lingvo.site.state.ProjectAuthority
import org.jetbrains.compose.web.css.FlexWrap
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Li
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text
import org.jetbrains.compose.web.dom.Ul

/** Full declared counts and scope precede a possibly capped sample; unknown codes remain visible. */
@Composable
internal fun ReviewedCurrentReleaseResult(report: ReleaseCheckReportDto, authority: ProjectAuthority, onNavigate: (String) -> Unit) {
    Column(Modifier.fillMaxWidth().gap(Space.MD).attr("data-reviewed-release-result", "true")
        .attr("data-release-passed", report.passed.toString()).attr("data-release-complete", report.complete.toString())
        .attr("data-release-evaluated-at", report.evaluatedAt).attr("data-release-locales", report.scope.locales.joinToString(","))) {
        LMuted(tf("release.reviewed_current_release_result.last_checked", "Last checked: {time}", "time" to formatUtc(report.evaluatedAt)))
        LMuted(tf("release.reviewed_current_release_result.exact_hosted_scope", "Hosted project: {project}. Source: {source}. Checked targets: {targets}.",
            "project" to report.scope.projectSlug, "source" to report.scope.sourceLocale, "targets" to report.scope.locales.joinToString(", ")))
        LMuted(tf("release.reviewed_current_release_result.full_counts", "Files: {files} · Source keys: {keys} · Target strings: {targets} · Protected/non-translatable strings: {excluded}",
            "files" to formatCount(report.counts.logicalFiles), "keys" to formatCount(report.counts.sourceKeys),
            "targets" to formatCount(report.counts.targetStrings), "excluded" to formatCount(report.counts.excludedKeys)))
        LSectionTitle(t("release.reviewed_current_release_result.exact_file_scope", "Files in this check"))
        Ul(Modifier.styleModifier { property("overflow-wrap", "anywhere") }.toAttrs()) {
            report.scope.filePaths.forEach { path -> Li { Text(path) } }
        }
        Row(Modifier.flexWrap(FlexWrap.Wrap).gap(Space.MD)) {
            LMuted(tf("release.reviewed_current_release_result.error_count", "Errors: {count}", "count" to formatCount(report.counts.errors)))
            LMuted(tf("release.reviewed_current_release_result.warning_count", "Warnings: {count}", "count" to formatCount(report.counts.warnings)))
        }
        if (report.counts.byCode.isNotEmpty()) {
            LSectionTitle(t("release.reviewed_current_release_result.counted_reasons", "Counted reasons across this scope"))
            report.counts.byCode.forEach { (code, count) ->
                Div(attrs = { attr("data-release-reason", code) }) {
                    Span { Text(reasonLabel(code)) }
                    Text(": ")
                    Text(formatCount(count))
                }
            }
        }
        LMuted(tf("release.reviewed_current_release_result.shown_findings", "Findings shown: {shown} / {total}",
            "shown" to formatCount(report.findings.size), "total" to formatCount(report.counts.findings)),
            modifier = Modifier.attr("data-release-findings-count", "true").attr("data-shown", report.findings.size.toString())
                .attr("data-total", report.counts.findings.toString()))
        if (report.truncated) LMuted(t("release.reviewed_current_release_result.capped_sample", "The findings below are a sample. The totals above cover the entire checked scope."))
        report.findings.forEach { finding ->
            Column(Modifier.fillMaxWidth().gap(Space.SM)) {
                LMuted(finding.message)
                LMuted(listOfNotNull(finding.filePath, finding.resourcePath, finding.locale, finding.keyName, finding.code).joinToString(" · "))
                recoveryPath(report, finding)?.let { path ->
                    val writable = authority.canWriteTranslations && (finding.locale != report.scope.sourceLocale || authority.canEditStructure)
                    LButton(if (writable) t("release.reviewed_current_release_result.review_string", "Review string")
                        else t("release.reviewed_current_release_result.inspect_string", "Inspect string"), { onNavigate(path) },
                        variant = LButtonVariant.GHOST, size = LButtonSize.SM,
                        accessibleLabel = tf("release.reviewed_current_release_result.open_exact_string", "Open {key} in {locale}",
                            "key" to (finding.keyName ?: finding.keyId), "locale" to finding.locale))
                }
            }
        }
    }
}

/** The concise verdict is live; the potentially long scope and diagnostic list stays ordinary text. */
@Composable
internal fun ReviewedCurrentReleaseStatus(report: ReleaseCheckReportDto?) {
    Div(attrs = {
        attr("role", "status")
        attr("aria-live", "polite")
        attr("aria-atomic", "true")
        attr("data-reviewed-release-status", "true")
    }) {
        if (report != null) {
            val verdict = when {
                !report.complete -> t("release.reviewed_current_release_result.check_incomplete", "Check incomplete")
                report.passed -> t("release.reviewed_current_release_result.checked_scope_passed", "Checked scope passed")
                else -> t("release.reviewed_current_release_result.checked_scope_needs_attention", "Checked scope needs attention")
            }
            LBadge(verdict, variant = if (report.passed && report.complete) LBadgeVariant.SUCCESS else LBadgeVariant.WARNING)
        }
    }
}

@Composable
private fun reasonLabel(code: String): String = when (code) {
    "unapproved" -> t("release.reviewed_current_release_result.needs_approval", "Needs approval")
    "stale_basis" -> t("release.reviewed_current_release_result.source_changed", "Source changed")
    "unknown_basis" -> t("release.reviewed_current_release_result.source_basis_not_recorded", "Source basis not recorded")
    "unresolved_question" -> t("release.reviewed_current_release_result.unanswered_questions", "Unanswered questions")
    "review_pending" -> t("release.reviewed_current_release_result.review_pending", "Review pending")
    "qa_error" -> t("release.reviewed_current_release_result.qa_errors", "QA errors")
    "qa_warning" -> t("release.reviewed_current_release_result.qa_warnings", "QA warnings")
    "source_unavailable" -> t("release.reviewed_current_release_result.source_unavailable", "Source unavailable")
    "target_unavailable" -> t("release.reviewed_current_release_result.translation_unavailable", "Translation unavailable")
    "incomplete_target" -> t("release.reviewed_current_release_result.translation_incomplete", "Translation incomplete")
    "unsupported_projection" -> t("release.reviewed_current_release_result.unsupported_resource_form", "Unsupported resource form")
    "source_mismatch" -> t("release.reviewed_current_release_result.source_does_not_match", "Source does not match")
    "target_mismatch" -> t("release.reviewed_current_release_result.translation_does_not_match", "Translation does not match")
    "translatable_mismatch" -> t("release.reviewed_current_release_result.protection_does_not_match", "Protection does not match")
    "excluded_target_mismatch" -> t("release.reviewed_current_release_result.protected_translation_changed", "Protected translation changed")
    else -> code
}

private fun recoveryPath(report: ReleaseCheckReportDto, finding: ReleaseCheckFindingDto): String? {
    val project = report.scope.projectSlug ?: return null
    val key = finding.keyId ?: return null
    val locale = finding.locale?.takeIf { it in report.scope.locales || it == report.scope.sourceLocale } ?: return null
    return "/app/projects/${encodeURIComponent(project)}/translate/${encodeURIComponent(locale)}?key=${encodeURIComponent(key)}" +
        finding.keyName?.let { "&search=${encodeURIComponent(it)}" }.orEmpty()
}

private external fun encodeURIComponent(value: String): String
